import React, { useState } from 'react';
import cn from 'classnames';

import { SearchWidgetPreview } from '@lt/components';

import type { DateCell } from '@lt/widget/components/Cigarette/components/DatePicker/types';
import { convertDatesToStringWithEllipsis } from '@lt/widget/components/Cigarette/utils';
import { SEARCH_WIDGET_PLACE_ID } from 'src/constants/header';
import { isSSR } from 'src/helpers/ssr';
import type { SearchType } from 'src/types/search';
import TouristLimitModal from 'src/components/TouristLimitModal';
import { getDateForDisplay } from 'src/utils/cigaretteUtils';
import HeaderSearchForm from '../HeaderSearchForm';

import type { PropsFromRedux } from './index';
import styles from './headerSearchWidgetPreview.module.css';
import { useTouristsLimitCount } from './useTouristsLimitCount';

export const HeaderSearchWidgetPreview = ({
  showHeaderPopup,
  onOpenModal,
  onCloseModal,
  isMobile,
  isWhitelabel,
  cigaretteValues,
  className,
  pageSearchType,
  availableSearchTypes,
  initialOpenedField,
}: PropsFromRedux & { className?: string }) => {
  // Хак для страницы отеля, где availableSearchTypes имеют тип строки
  const availableSearchTypesArray =
    typeof availableSearchTypes === 'string'
      ? JSON.parse(availableSearchTypes)
      : availableSearchTypes;

  const searchType =
    pageSearchType ||
    (availableSearchTypes &&
      availableSearchTypes.length >= 1 &&
      availableSearchTypes[0]) ||
    'package';
  const [currentSearchType, setCurrentSearchType] =
    useState<SearchType>(searchType);

  const { tourists } = cigaretteValues || {};

  const {
    adultsValue,
    kidsValue,
    showTourisLimitPopup,
    setShowTourisLimitPopup,
  } = useTouristsLimitCount(currentSearchType, showHeaderPopup, tourists);

  if (isSSR) return null;

  const getDateString = (dateCell: DateCell) =>
    convertDatesToStringWithEllipsis({
      ...dateCell,
      date: getDateForDisplay(dateCell.date),
    });

  const startDate = cigaretteValues?.datepicker
    ? getDateString(cigaretteValues.datepicker.left)
    : undefined;

  const endDate = cigaretteValues?.datepicker
    ? getDateString(cigaretteValues.datepicker.right)
    : undefined;

  const setCurrentSearchTypeOnModal = (searchTypeParam?: SearchType) => {
    if (
      searchTypeParam === 'package' ||
      searchTypeParam === 'hotel' ||
      searchTypeParam === 'trip'
    )
      setCurrentSearchType(searchTypeParam);
    else {
      setCurrentSearchType(searchType);
    }
    onOpenModal();
  };

  return (
    <SearchWidgetPreview
      className={cn(styles.searchWidgetPreview, className)}
      isWhiteLabel={isWhitelabel}
      availableSearchTypes={availableSearchTypesArray}
      showPopup={showHeaderPopup}
      onOpenModal={setCurrentSearchTypeOnModal}
      onCloseModal={onCloseModal}
      destinationName={cigaretteValues?.destination.label}
      startDate={startDate}
      endDate={endDate}
      personsCount={
        (cigaretteValues?.tourists?.adults || 0) +
        (cigaretteValues?.tourists?.kids.length || 0)
      }
      isMobile={isMobile}
      PopupComponent={
        <div
          className={cn(styles.widgetWrapper, {
            [styles.mobileWidgetWrapper]: isMobile,
            [styles.wlWidgetWrapper]: isWhitelabel,
          })}
          id={SEARCH_WIDGET_PLACE_ID}
        >
          <HeaderSearchForm
            cigaretteValues={{
              ...cigaretteValues,
              tourists: { adults: adultsValue, kids: kidsValue },
            }}
            pageSearchType={currentSearchType}
            isMobile={isMobile}
            availableSearchTypes={availableSearchTypes}
            inPreview
            initialOpenedField={initialOpenedField}
          />

          <TouristLimitModal
            isOpen={showTourisLimitPopup}
            isMobile={isMobile}
            onClose={() => setShowTourisLimitPopup(false)}
          />
        </div>
      }
      searchType={searchType}
    />
  );
};
